import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { motion } from "framer-motion";
import { Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { useI18n } from "@/lib/i18n";

interface ProcessStatusDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  status: 'idle' | 'processing' | 'success' | 'error';
  step: string;
  progress: number;
  error?: string;
}

const ProcessStatusDialog = ({ open, onOpenChange, status, step, progress, error }: ProcessStatusDialogProps) => {
  const { t } = useI18n();

  const getStepLabel = (step: string) => {
    switch (step) {
      case 'collecting': return t('storage.process_collecting') || '데이터 수집 중...';
      case 'compressing': return t('storage.process_compressing') || '데이터 압축 중...';
      case 'decompressing': return t('storage.process_decompressing') || '데이터 압축 해제 중...';
      case 'restoring': return t('storage.process_restoring') || '데이터 복구 중...';
      case 'finalizing': return t('storage.process_finalizing') || '마무리 중...';
      case 'done': return t('storage.process_done') || '완료되었습니다!';
      default: return step;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-background/95 backdrop-blur-xl border-primary/20 shadow-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {status === 'processing' && <Loader2 className="w-5 h-5 animate-spin text-primary" />}
            {status === 'success' && <CheckCircle2 className="w-5 h-5 text-green-500" />}
            {status === 'error' && <AlertCircle className="w-5 h-5 text-destructive" />}
            {status === 'processing' ? t('storage.processing') || '처리 중' : 
             status === 'success' ? t('storage.complete') || '완료' : 
             t('storage.error') || '오류'}
          </DialogTitle>
        </DialogHeader>

        <div className="py-6 flex flex-col gap-4">
          <div className="flex justify-between text-sm text-muted-foreground font-medium">
            <span>{getStepLabel(step)}</span>
            <span>{Math.round(progress)}%</span>
          </div>

          <div className="h-2 w-full bg-muted rounded-full overflow-hidden">
            <motion.div
              className={`h-full ${status === 'error' ? 'bg-destructive' : 'bg-primary'}`}
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>

          {status === 'error' && (
            <p className="text-xs text-destructive bg-destructive/10 p-2 rounded border border-destructive/20 mt-2">
              {error || '알 수 없는 오류가 발생했습니다.'}
            </p>
          )}

          {status === 'success' && (
            <p className="text-xs text-muted-foreground text-center mt-2 italic">
              {t('storage.refresh_tip') || '화면을 새로고침하여 변경 사항을 확인하세요.'}
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProcessStatusDialog;
